/**
 * PatientEditPage - Dedicated page for editing patient information
 * Uses FormPageLayout and standard platform components
 * Family portal invites, approval and revoke live on the chart's Family Portal tab (Phase 12)
 */
import React, { useState, useEffect, useRef } from 'react';
import { Camera, ChevronDown, ChevronUp, ChevronsDown, ChevronsUp, User, Users } from 'lucide-react';
import FormPageLayout, { ActionButtonConfig } from '../../common/FormPageLayout';
import LoadingSpinner from '../../common/LoadingSpinner';
import { PatientFormState, INITIAL_PATIENT_FORM, GENDER_MAP } from './types';
import { validatePatientForm, formatDateForDisplay, parseFullName, formatDateForInput } from './utils';
import { updatePatient, fetchPatients } from '../../../services/admin/patient.service';
import { patientImageService } from '../../../services/admin/patientImage.service';
import { PatientFormData } from '../../../types/admin/patient';
import { useCanPerform } from '../../../hooks/useCanPerform';

interface PatientEditPageProps {
  patientId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const PatientEditPage: React.FC<PatientEditPageProps> = ({ patientId, onSuccess, onCancel }) => {
  // Check permissions for updating patients (requires admin:patients:phi:update permission)
  const canUpdatePatient = useCanPerform('update:patients:phi');


  const [formData, setFormData] = useState<PatientFormState>(INITIAL_PATIENT_FORM);
  const [mrn, setMrn] = useState('');

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapsedSections, setCollapsedSections] = useState({
    'Patient Information': false,
    'Family Portal': false,
  });

  const imageInputRef = useRef<HTMLInputElement>(null);

  // Color mapping for sections matching assessment form design system
  const PATIENT_FORM_SECTION_COLORS = {
    'Patient Information': {
      bg: 'bg-white',
      border: 'border-gray-200',
      headerBg: 'bg-slate-50',
      headerHover: 'hover:bg-slate-100',
      iconColor: 'text-gray-600'
    },
    'Family Portal': {
      bg: 'bg-white',
      border: 'border-gray-200',
      headerBg: 'bg-slate-50',
      headerHover: 'hover:bg-slate-100',
      iconColor: 'text-gray-600'
    }
  };

  const getPatientSectionIcon = (sectionName: string) => {
    const icons: Record<string, React.ReactNode> = {
      'Patient Information': <User size={18} className={`mr-2 ${PATIENT_FORM_SECTION_COLORS[sectionName]?.iconColor || 'text-gray-600'}`} />,
      'Family Portal': <Users size={18} className={`mr-2 ${PATIENT_FORM_SECTION_COLORS[sectionName]?.iconColor || 'text-gray-600'}`} />
    };
    return icons[sectionName] || null;
  };

  const toggleSection = (sectionName: string) => {
    setCollapsedSections(prev => ({ ...prev, [sectionName]: !prev[sectionName] }));
  };

  const toggleAllSections = (expand: boolean) => {
    setCollapsedSections({
      'Patient Information': !expand,
      'Family Portal': !expand,
    });
  };


  // Load patient data on mount
  useEffect(() => {
    const loadPatient = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await fetchPatients(1, 100, '', undefined);
        const patient = response.patients.find((p: any) => p.id === patientId);

        if (patient) {
          const { firstName, middleName, lastName } = parseFullName(
            `${patient.firstName} ${patient.lastName}`
          );

          setFormData({
            firstName,
            middleName,
            lastName,
            dob: formatDateForInput(patient.dob),
            gender: patient.gender as any,
            email: patient.email || '',
            phone: patient.phoneNumber || '',
            status: patient.status,
          });

          setMrn(patient.mrn || '');

          const imageResult = await patientImageService.getPatientImage(patientId);
          if (imageResult.hasProfilePicture && imageResult.imageUrl) {
            setExistingImageUrl(imageResult.imageUrl);
          }
        } else {
          setError('Patient not found');
        }
      } catch (err) {
        console.error('Failed to load patient:', err);
        setError('Failed to load patient information');
      } finally {
        setLoading(false);
      }
    };

    loadPatient();
  }, [patientId]);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    const validationErrors = validatePatientForm(formData);
    if (validationErrors.length > 0) {
      setError(validationErrors.join('\n'));
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const patientFormData: PatientFormData = {
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        dob: formData.dob,
        gender: GENDER_MAP.toAPI(formData.gender),
        email: formData.email.trim(),
        phoneNumber: formData.phone.trim(),
        status: formData.status,
      };

      await updatePatient(patientId, patientFormData);

      // Upload image if selected (non-blocking)
      if (imageFile) {
        patientImageService.uploadPatientImage(patientId, imageFile).catch(() => {
          console.error('Failed to upload patient image (patient was saved)');
        });
      }

      if (onSuccess) {
        onSuccess();
      }
    } catch (err) {
      console.error('Failed to update patient:', err);
      setError('Failed to update patient. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const actions: ActionButtonConfig[] = canUpdatePatient ? [
    {
      key: 'cancel',
      label: 'Cancel',
      onClick: onCancel || (() => {}),
      variant: 'secondary',
      disabled: submitting,
    },
    {
      key: 'save',
      label: 'Save Changes',
      onClick: handleSave,
      variant: 'primary',
      loading: submitting,
      loadingLabel: 'Saving...',
      disabled: submitting,
    },
  ] : [];

  const getInitials = (): string => {
    return (
      (formData.firstName?.charAt(0)?.toUpperCase() || '') +
      (formData.lastName?.charAt(0)?.toUpperCase() || '')
    );
  };

  if (loading) {
    return (
      <FormPageLayout onBack={onCancel || (() => {})} actions={[]} title="Edit Patient">
        <LoadingSpinner label="Loading patient information" className="py-12" />
      </FormPageLayout>
    );
  }

  return (
    <>
      <FormPageLayout onBack={onCancel || (() => {})} actions={actions} title="Edit Patient">
        <div className="px-4 py-6 w-full">
          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-700 whitespace-pre-line">{error}</p>
            </div>
          )}

        {/* Expand All / Collapse All Button */}
        <div className="mb-6 flex justify-end gap-2">
          <button
            onClick={() => toggleAllSections(true)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md border border-gray-300 transition-colors"
          >
            <ChevronsDown size={16} />
            <span>Expand All</span>
          </button>
          <button
            onClick={() => toggleAllSections(false)}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md border border-gray-300 transition-colors"
          >
            <ChevronsUp size={16} />
            <span>Collapse All</span>
          </button>
        </div>

        {/* Patient Information Section */}
        <div className={`rounded-lg border ${PATIENT_FORM_SECTION_COLORS['Patient Information'].border} shadow-sm ${PATIENT_FORM_SECTION_COLORS['Patient Information'].bg} mb-6`}>
          {/* Clickable Header */}
          <div
            className={`flex items-center justify-between cursor-pointer p-4 ${PATIENT_FORM_SECTION_COLORS['Patient Information'].headerBg} ${PATIENT_FORM_SECTION_COLORS['Patient Information'].headerHover} rounded-t-lg transition-colors`}
            onClick={() => toggleSection('Patient Information')}
          >
            <div className="flex items-center">
              {getPatientSectionIcon('Patient Information')}
              <h2 className="text-base font-medium text-gray-900">Patient Information</h2>
            </div>
            {collapsedSections['Patient Information'] ? (
              <ChevronDown size={16} className="text-gray-500" />
            ) : (
              <ChevronUp size={16} className="text-gray-500" />
            )}
          </div>

          {/* Content — conditionally rendered */}
          {!collapsedSections['Patient Information'] && (
          <div className="p-4 border-t border-gray-200 space-y-6">
            {/* Photo Upload */}
            <div className="flex flex-col items-center">
              <input
                ref={imageInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="relative h-24 w-24 rounded-full bg-slate-700 text-white flex items-center justify-center overflow-hidden group cursor-pointer"
              >
                {imagePreview ? (
                  <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                ) : existingImageUrl ? (
                  <img src={existingImageUrl} alt="Current" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-2xl font-bold">{getInitials()}</span>
                )}
                <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <Camera size={24} className="text-white" />
                </div>
              </button>
              <button
                type="button"
                onClick={() => imageInputRef.current?.click()}
                className="mt-3 text-sm text-blue-600 hover:text-blue-700 font-medium"
              >
                {imagePreview || existingImageUrl ? 'Change Photo' : 'Upload Photo'}
              </button>
            </div>

            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name <span className="text-red-600">*</span>
                </label>
                <input
                  disabled={!canUpdatePatient}
                  type="text"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className={`w-full p-2 border rounded text-base ${
                    !canUpdatePatient 
                      ? 'bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed' 
                      : 'border-gray-300'
                  }`}
                  placeholder="First name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Middle Name
                </label>
                <input
                  disabled={!canUpdatePatient}
                  type="text"
                  value={formData.middleName}
                  onChange={(e) => setFormData({ ...formData, middleName: e.target.value })}
                  className={`w-full p-2 border rounded text-base ${
                    !canUpdatePatient 
                      ? 'bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed' 
                      : 'border-gray-300'
                  }`}
                  placeholder="Middle name (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name <span className="text-red-600">*</span>
                </label>
                <input
                  disabled={!canUpdatePatient}
                  type="text"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                  className={`w-full p-2 border rounded text-base ${
                    !canUpdatePatient 
                      ? 'bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed' 
                      : 'border-gray-300'
                  }`}
                  placeholder="Last name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">MRN</label>
                <input
                  type="text"
                  value={mrn}
                  disabled
                  className="w-full p-2 border rounded text-base bg-gray-50 border-gray-300 text-gray-500"
                />
                <p className="mt-1 text-xs text-gray-600">
                  Medical Record Number (cannot be edited)
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth <span className="text-red-600">*</span>
                </label>
                <input
                  disabled={!canUpdatePatient}
                  type="date"
                  value={formData.dob}
                  onChange={(e) => setFormData({ ...formData, dob: e.target.value })}
                  className={`w-full p-2 border rounded text-base ${
                    !canUpdatePatient 
                      ? 'bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed' 
                      : 'border-gray-300'
                  }`}
                />
                {formData.dob && (
                  <p className="mt-1 text-xs text-gray-600">
                    Displayed as: {formatDateForDisplay(formData.dob)}
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender <span className="text-red-600">*</span>
                </label>
                <select
                  disabled={!canUpdatePatient}
                  value={GENDER_MAP.toUI(formData.gender)}
                  onChange={(e) => setFormData({ ...formData, gender: GENDER_MAP.toAPI(e.target.value) as any })}
                  className={`w-full p-2 border rounded text-base ${
                    !canUpdatePatient 
                      ? 'bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed' 
                      : 'border-gray-300'
                  }`}
                >
                  <option value="">Select gender</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            {/* Email and Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  disabled={!canUpdatePatient}
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className={`w-full p-2 border rounded text-base ${
                    !canUpdatePatient 
                      ? 'bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed' 
                      : 'border-gray-300'
                  }`}
                  placeholder="email@example.com (optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone <span className="text-red-600">*</span>
                </label>
                <input
                  disabled={!canUpdatePatient}
                  type="text"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className={`w-full p-2 border rounded text-base ${
                    !canUpdatePatient 
                      ? 'bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed' 
                      : 'border-gray-300'
                  }`}
                  placeholder="(555) 123-4567"
                />
              </div>
            </div>

            {/* Status */}
            <div className="w-full md:w-1/2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status <span className="text-red-600">*</span>
              </label>
              <select
                disabled={!canUpdatePatient}
                value={formData.status}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    status: e.target.value as 'PENDING_ADMISSION' | 'ACTIVE' | 'INACTIVE' | 'ON_HOLD',
                  })
                }
                className={`w-full p-2 border rounded text-base ${
                  !canUpdatePatient 
                    ? 'bg-gray-100 text-gray-600 border-gray-300 cursor-not-allowed' 
                    : 'border-gray-300'
                }`}
              >
                <option value="ACTIVE">ACTIVE</option>
                <option value="ON_HOLD">ON HOLD</option>
                <option value="INACTIVE">INACTIVE</option>
              </select>
            </div>
          </div>
          )}
        </div>

        {/* Family Portal Section */}
        <div className={`rounded-lg border ${PATIENT_FORM_SECTION_COLORS['Family Portal'].border} shadow-sm ${PATIENT_FORM_SECTION_COLORS['Family Portal'].bg}`}>
          {/* Clickable Header */}
          <div
            className={`flex items-center justify-between cursor-pointer p-4 ${PATIENT_FORM_SECTION_COLORS['Family Portal'].headerBg} ${PATIENT_FORM_SECTION_COLORS['Family Portal'].headerHover} rounded-t-lg transition-colors`}
            onClick={() => toggleSection('Family Portal')}
          >
            <div className="flex items-center">
              {getPatientSectionIcon('Family Portal')}
              <h2 className="text-base font-medium text-gray-900">Family Portal</h2>
            </div>
            {collapsedSections['Family Portal'] ? (
              <ChevronDown size={16} className="text-gray-500" />
            ) : (
              <ChevronUp size={16} className="text-gray-500" />
            )}
          </div>

          {/* Content — conditionally rendered */}
          {!collapsedSections['Family Portal'] && (
          <div className="p-4 border-t border-gray-200">
            <p className="text-sm text-gray-700">
              Guardian portal invites, approvals and revoking access are managed on the patient&apos;s chart:
              open the patient, then <span className="font-medium">Case Management → Family Portal</span>.
              Legal guardians and their email addresses are recorded on the Demographics tab.
            </p>
          </div>
          )}
        </div>
      </div>
    </FormPageLayout>

    </>
  );
};

export default PatientEditPage;
